import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { fg } from '@atlaskit/platform-feature-flags';

import SharedSalesforceDynamicMultiSelect from '../../../../../common/ui/filters/product-filters/shared-salesforce-dynamic-multi-select';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import {
	useFiltersDiscoverability2Enabled,
	useIsConfigLoaded,
} from '../../../../../controllers/store/bootstrap';
import { filter as campaignStatusFilter } from '../../../../../controllers/store/search/filters/campaign-status';

import { i18n } from './messages';

const CampaignStatusFilter = (props: FilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [filterData, { loadInitialOptions, setOption, clearOptions }] = useSelectFilter({
		filterId: campaignStatusFilter.id,
	});
	const isCampaignStatusFilterEnabled = fg('rovo-fps-3p-ux-sf-campaign-status-filter');
	const isRetryOnConfigLoadEnabled = fg('rovo-fps-3p-ux-sf-retry-on-config-load');
	const isConfigLoaded = useIsConfigLoaded();
	// See SalesforceStageFilter for the rationale: when the retry FF is on, hold the
	// "did mount" flip until isConfigLoaded is true so the bootstrap-config race
	// cannot leave the filter permanently empty.
	const hasLoadedInitialOptions = useRef(false);

	useEffect(() => {
		if (!isCampaignStatusFilterEnabled || hasLoadedInitialOptions.current) {
			return;
		}
		if (isRetryOnConfigLoadEnabled && !isConfigLoaded) {
			return;
		}
		hasLoadedInitialOptions.current = true;
		loadInitialOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCampaignStatusFilterEnabled, isRetryOnConfigLoadEnabled, isConfigLoaded]);

	if (!filterData || !isCampaignStatusFilterEnabled) {
		return null;
	}

	const { isLoading, suggestedOptions, selectedOptions } = filterData;

	return (
		<SharedSalesforceDynamicMultiSelect
			{...props}
			filterId={campaignStatusFilter.id}
			label={formatMessage(i18n.campaignStatusFilterLabel)}
			noOptionsMessage={formatMessage(i18n.campaignStatusFilterNoOptions)}
			iconBefore={<StatusInformationIcon label="" />}
			isLoading={isLoading}
			selectedOptions={selectedOptions}
			suggestedOptions={suggestedOptions}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			loadInitialOptions={loadInitialOptions}
			setOption={(option) => setOption(option)}
			clearOptions={clearOptions}
		/>
	);
};

export default CampaignStatusFilter;
